import React, { useState } from 'react';
import { AppShell, NotificationBell, SearchResults, flattenResults, defaultNavGroups } from '../../components/shell';
import { GlobalSearchData } from '../../client/searchClient';
import Section from './Section';
import { sampleNotifications, sampleUnreadCount } from './notificationFixtures';

/**
 * /design — app shell v2 states, side by side with the
 * `Dashboard v2.dc.html` chrome: navy nav rail (active item, count badge,
 * live dots, honest "soon" rows, user card + sign-out), white top bar
 * (title/date, search UI, bell, + New RFQ). Resize the browser to ~924px to
 * check the clamp() rails.
 */
// Sample payload for the dropdown states below (design reference only — the
// live top bar always renders real `GET /trp/search` results).
const sampleResults: GlobalSearchData = {
  query: 'motor',
  limitPerGroup: 5,
  rfqs: [
    { rfqId: 'RFQ-042', name: 'Gripper actuator refresh', status: 'RFQ_SENT', updatedAt: '2026-07-20T10:00:00Z' },
    { rfqId: 'RFQ-038', name: 'Servo motor sourcing', status: 'DRAFT', updatedAt: '2026-07-18T10:00:00Z' },
  ],
  suppliers: [
    { name: 'Oriental Motor', status: 'CONTACTED', rfqId: 'RFQ-042', rfqName: 'Gripper actuator refresh', componentId: 'COMP-1', componentName: 'Stepper motor NEMA-17' },
  ],
  parts: [
    { componentId: 'COMP-1', name: 'Stepper motor NEMA-17', manufacturer: 'Oriental Motor', rfqId: 'RFQ-042', rfqName: 'Gripper actuator refresh' },
    { componentId: 'COMP-7', name: 'Motor driver TMC2209', manufacturer: null, rfqId: 'RFQ-038', rfqName: 'Servo motor sourcing' },
  ],
  truncated: { rfqs: false, suppliers: false, parts: true },
};
const emptyResults: GlobalSearchData = {
  query: 'zzz', limitPerGroup: 5, rfqs: [], suppliers: [], parts: [],
  truncated: { rfqs: false, suppliers: false, parts: false },
};

const DropdownState: React.FC<{
  label: string; data: GlobalSearchData | null; loading?: boolean; error?: boolean;
  recents?: string[]; activeIndex?: number;
}> = ({ label, data, loading = false, error = false, recents = [], activeIndex = -1 }) => {
  const showRecents = recents.length > 0;
  const items = flattenResults(data, recents, showRecents);
  return (
    <div className="w-[280px]">
      <p className="mb-s2 text-meta text-ink-muted">{label}</p>
      {/* Spacer reserves the drop area; the zero-height relative anchor makes
          the absolutely-positioned panel open right below the label. */}
      <div className="h-[360px]">
        <div className="relative">
        <SearchResults
          query={data?.query ?? 'motor'}
          data={data}
          loading={loading}
          error={error}
          items={items}
          showRecents={showRecents}
          activeIndex={activeIndex}
          onSelect={() => undefined}
          onHover={() => undefined}
        />
        </div>
      </div>
    </div>
  );
};

const ShellGallery: React.FC = () => {
  const [active, setActive] = useState('overview');
  const groups = defaultNavGroups.map((g) => ({
    ...g,
    items: g.items.map((item) => (item.id === 'rfqs' ? { ...item, count: 6 } : item)),
  }));

  return (
    <Section title="App shell (navy rail + top bar)">
      <div className="overflow-hidden rounded-card border border-line">
        <AppShell
          className="h-[560px]"
          sidebar={{
            groups,
            activeId: active,
            onNavigate: setActive,
            user: { name: 'Priya Nair', role: 'Procurement Lead' },
            onLogout: () => undefined,
          }}
          topBar={{
            title: 'Overview',
            date: 'Monday, 20 Jul 2026',
            notificationsSlot: (
              <NotificationBell
                unreadCount={sampleUnreadCount}
                items={sampleNotifications}
                onSelect={() => undefined}
                onMarkAllRead={() => undefined}
              />
            ),
            onAction: () => undefined,
          }}
        >
          <div className="flex h-full items-center justify-center rounded-card border border-dashed border-line-strong">
            <span className="text-body-md text-ink-muted">Page content renders on the #F4F6FA canvas (max-w-content where applicable)</span>
          </div>
        </AppShell>
      </div>
      <div className="mt-s6">
        <p className="mb-s3 text-row-title text-ink">Global search dropdown states</p>
        <div className="flex flex-wrap gap-s6">
          <DropdownState label="Grouped results (keyboard row 2 active, parts truncated)" data={sampleResults} activeIndex={2} />
          <DropdownState label="No matches" data={emptyResults} />
          <DropdownState label="Loading" data={null} loading />
          <DropdownState label="Backend unreachable" data={null} error />
          <DropdownState label="Recent searches (before typing)" data={null} recents={['nema 17', 'oriental motor', 'RFQ-042']} />
        </div>
      </div>
    </Section>
  );
};

export default ShellGallery;
